import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useJWTAuth } from "../../context/JWTAuthContext";
import { useNotifications } from "../../context/NotificationsContext";
import { motion } from "framer-motion";

// Icons
const HomeIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
    />
  </svg>
);
const ListIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
    />
  </svg>
);
const WalletIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
    />
  </svg>
);
const BellIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
);
const MenuIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);
const GraduationIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
    />
  </svg>
);
const UsersIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
);
const BookIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
    />
  </svg>
);

const BottomNavItem = ({ to, icon: Icon, label, badge, active, onClick }) => {
  return (
    <div className="flex-1 flex flex-col items-center justify-center relative">
      <Link
        to={to}
        onClick={onClick}
        className={`flex flex-col items-center justify-center w-full h-full pt-2 pb-1 transition-colors ${
          active
            ? "text-[var(--color-primary)]"
            : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
        }`}
      >
        <div className="relative">
          <Icon />
          {badge > 0 && (
            <span className="absolute -top-1 -right-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[16px] text-center shadow-sm border border-[var(--color-bg-secondary)]">
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </div>
        <span className="text-[10px] font-medium mt-1">{label}</span>
      </Link>
      {active && (
        <motion.div
          layoutId="bottomNavIndicator"
          className="absolute top-0 w-8 h-1 bg-[var(--color-primary)] rounded-b-full"
        />
      )}
    </div>
  );
};

const BottomNavigation = ({ mobileMenuOpen, setMobileMenuOpen }) => {
  const location = useLocation();
  const { hasRole } = useJWTAuth();
  const { unreadCount } = useNotifications();

  // Полноэкранный мессенджер-вид: на /support нижняя навигация скрыта
  if (location.pathname.startsWith("/support")) return null;

  const isSuperAdmin = hasRole("superadmin") || hasRole("megaadmin");
  const isTutor = hasRole("tutor");

  const isActive = (path) => location.pathname === path;
  const isTutorSectionActive =
    location.pathname === "/tutor" ||
    location.pathname === "/tutor-list" ||
    location.pathname === "/tutor/class-orders" ||
    location.pathname === "/tutor/student-orders";

  return (
    <>
      {/* Main Bottom Nav Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-[var(--color-bg-secondary)] border-t border-[var(--color-border)] pb-safe z-[var(--z-index-bottom-nav)]">
        <div className="flex items-center h-[60px] max-w-md mx-auto relative">
          {/* Common: Dashboard */}
          <BottomNavItem
            to="/dashboard"
            icon={HomeIcon}
            label="Главная"
            active={isActive("/dashboard")}
          />

          {/* --- ROLE BASED LINKS --- */}

          {/* 1. TUTOR */}
          {isTutor && !isSuperAdmin && (
            <>
              <BottomNavItem
                to="/tutor"
                icon={GraduationIcon}
                label="Мой класс"
                active={isTutorSectionActive}
              />

              <BottomNavItem
                to="/orders"
                icon={ListIcon}
                label="Мои заказы"
                active={isActive("/orders")}
              />
            </>
          )}

          {/* 2. ADMIN */}
          {isSuperAdmin && (
            <>
              <BottomNavItem
                to="/admin/user-management"
                icon={UsersIcon}
                label="Юзеры"
                active={isActive("/admin/user-management")}
              />
              <BottomNavItem
                to="/canteen-week-orders"
                icon={ListIcon}
                label="Заказы"
                active={isActive("/canteen-week-orders")}
              />
              <BottomNavItem
                to="/admin/menu-history"
                icon={BookIcon}
                label="Меню"
                active={isActive("/admin/menu-history")}
              />
            </>
          )}

          {/* 3. PARENT / STUDENT (Default) */}
          {!isTutor && !isSuperAdmin && (
            <>
              <BottomNavItem
                to="/orders"
                icon={ListIcon}
                label="Заказы"
                active={isActive("/orders")}
              />
              <BottomNavItem
                to="/profile/payments"
                icon={WalletIcon}
                label="Финансы"
                active={isActive("/profile/payments")}
              />
            </>
          )}

          {/* Common: Notifications */}
          <BottomNavItem
            to="/notifications"
            icon={BellIcon}
            label="Уведомления"
            badge={unreadCount}
            active={isActive("/notifications")}
          />

          {/* Common: Menu (Burger Trigger) */}
          <div className="flex-1 flex flex-col items-center justify-center relative">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`flex flex-col items-center justify-center w-full h-full pt-2 pb-1 transition-colors ${
                mobileMenuOpen
                  ? "text-[var(--color-primary)]"
                  : "text-[var(--color-text-secondary)]"
              }`}
            >
              <MenuIcon />
              <span className="text-[10px] font-medium mt-1">Меню</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BottomNavigation;
